import { cx } from "@emotion/css";
import Switch from "@mui/material/Switch";
import { useTranslation } from "react-i18next";
import isMobile from "src/helpers/isMobile";
import { fontSizes, useFontSize } from "src/hooks/useFontSize";
import LettersIcon from "src/images/Icons/newIcons/letters.svg";
import DarkIcon from "src/images/Icons/newIcons/settings/dark.svg";
import GesturesIcon from "src/images/Icons/newIcons/settings/gestures.svg";
import ShortcutsIcon from "src/images/Icons/newIcons/settings/shortcuts.svg";

import useShortcuts from "../../../hooks/useShortcuts";
import { useSwipeableEnabled } from "../../../hooks/useSwipeableEnabled";
import useTheme from "../../../hooks/useTheme";
import { Checkbox, FormControlLabel } from "../../Default/LekUI/Checkbox";
import Hint from "../../NewComponents/Default/Hint";

export const SettingsContainer = () => {
	const { fontSize, setFontSize, fontSizeClass } = useFontSize();
	const { theme, toggleTheme } = useTheme();
	const { toggleShortcuts, isShortcutsOn } = useShortcuts();
	const { toggleSwipeable, isSwipeableOn } = useSwipeableEnabled();
	const { t } = useTranslation();

	const renderFontSizes = () => {
		return fontSizes.map((size) => {
			return (
				<FormControlLabel
					key={size}
					onChange={() => setFontSize(size)}
					checked={String(fontSize) === size}
					value={size}
					label={
						<div className="flex-center-row gap-8">
							<LettersIcon className="icon-super-small" />
							<span className="size-16-light">{size}px</span>
						</div>
					}
					Control={<Checkbox />}
				/>
			);
		});
	};

	const renderShortcutsDetails = () => {
		return (
			<ul className="flex flex-col items-start gap-12 p-0">
				<li className="flex gap-12">
					<kbd>&larr;</kbd>-<span>{t("shortcuts.prevQuestion")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>&rarr;</kbd>-<span>{t("shortcuts.nextQuestion")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>a</kbd>-<span>{t("shortcuts.answerA")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>b</kbd>-<span>{t("shortcuts.answerB")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>c</kbd>-<span>{t("shortcuts.answerC")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>d</kbd>-<span>{t("shortcuts.answerD")}</span>
				</li>
				<li className="flex gap-12">
					<kbd>e</kbd>-<span>{t("shortcuts.answerE")}</span>
				</li>
				<li className="flex gap-12">
					<div className="flex gap-4">
						<kbd>⌘/⌃</kbd>+<kbd>a</kbd>
					</div>
					-<span>{t("shortcuts.article")}</span>
				</li>
				<li className="flex gap-12">
					<div className="flex gap-4">
						<kbd>⌘/⌃</kbd>+<kbd>o</kbd>
					</div>
					-<span>{t("shortcuts.discussion")}</span>
				</li>
				<li className="flex gap-12">
					<div className="flex gap-4">
						<kbd>⌘/⌃</kbd>+<kbd>u</kbd>
					</div>
					-<span>{t("shortcuts.favorite")}</span>
				</li>
				<li className="flex gap-12">
					<div className="flex gap-4">
						<kbd>⌘/⌃</kbd>+<kbd>k</kbd>
					</div>
					-<span>{t("shortcuts.comments")}</span>
				</li>
			</ul>
		);
	};

	return (
		<div>
			<div
				className={cx("flex items-start flex-col justify-between p-20 gap-18")}
			>
				<span className="size-18-light">{t("modals.settings.title")}</span>
				<div className="w-100">
					<span className={cx("size-16-light mt-0 mb-8")}>
						{t("modals.settings.fontSizeTitle")}:{" "}
						<span className={fontSizeClass}>Aa</span>
					</span>
					<div className="font-sizes--box">{renderFontSizes()}</div>
				</div>
				<div className="flex-center-row gap-12">
					<ShortcutsIcon className="icon-normal" />
					<span className={cx("size-16-light m-0")}>
						{t("modals.settings.shortcuts")}
					</span>
					<Hint
						hint={renderShortcutsDetails()}
						className=""
						Trigger={<div />}
						link=""
					/>
					<Switch
						className={cx("switch mx-0")}
						checked={isShortcutsOn}
						onChange={toggleShortcuts}
					/>
				</div>
				{isMobile() && (
					<div className="flex-center-row gap-12">
						<GesturesIcon className="icon-normal" />
						<span className={cx("size-16-light m-0")}>
							{t("modals.settings.swipeable")}
						</span>
						<Switch
							className={cx("switch mx-0")}
							checked={isSwipeableOn}
							onChange={toggleSwipeable}
						/>
					</div>
				)}
				<div className="flex-center-row gap-12">
					<DarkIcon className="icon-normal" />
					<span className={cx("size-16-light m-0")}>
						{t("modals.settings.darkMode")}
					</span>
					<Switch
						className={cx("switch mx-0")}
						checked={theme === "dark"}
						onChange={toggleTheme}
					/>
				</div>
			</div>
		</div>
	);
};
